import type { ReactNode } from 'react';
import { FONT_STACK, colors } from '../theme.js';

/**
 * L'attesa, mostrata.
 *
 * ## Due attese diverse, e una sola regola
 *
 * `06-RIPRODUZIONE §4` vieta la rotella **nuda** sull'avvio di un canale, e ha ragione: dopo sette
 * secondi una rotella che non dice niente è indistinguibile da una rotella eterna, la persona preme
 * Indietro e riprova, e peggiora la situazione. Ma il divieto riguarda il *silenzio*, non il
 * disegno: una schermata che non si muove affatto è peggio ancora, perché sembra bloccata.
 *
 * Quindi qui la rotella c'è sempre, e **il testo è obbligatorio**: `label` non è facoltativo. Non si
 * può, con questo componente, costruire l'attesa muta che il documento vieta — non per disciplina,
 * ma perché il tipo non lo permette.
 *
 * ## Le due forme
 *
 * - `hint` assente → un cambio di schermata, un catalogo che arriva. Attese brevi, dove basta dire
 *   *cosa* si sta aspettando.
 * - `hint` presente → l'avvio di un canale, dove il testo **cambia** col passare dei secondi e può
 *   portare la stima del server. Chi la passa è `startupPhase`, non questo componente: qui si
 *   mostra, non si decide.
 *
 * `back` non è decorativo: la seconda delle tre regole del §4 è che «indietro funziona sempre,
 * subito» — chi ha cambiato idea non deve aspettare il timeout. Dirlo sullo schermo è ciò che rende
 * l'attesa sopportabile, perché la trasforma in una scelta.
 */

export interface BusyProps {
  /** Cosa si sta aspettando. Obbligatorio: è il motivo per cui questo componente esiste. */
  readonly label: string;
  /** La riga che cambia: la fase dell'avvio, o la stima del server. */
  readonly hint?: string;
  /** Il titolo grande sopra l'attesa — il nome del canale, che si vede prima del video. */
  readonly title?: string;
  /** Cosa dice il tasto Indietro, quando c'è una via d'uscita immediata. */
  readonly back?: string;
  /** Riempie lo schermo invece di stare in linea. */
  readonly fullscreen?: boolean;
}

export function Busy({ label, hint, title, back, fullscreen = false }: BusyProps): ReactNode {
  return (
    <div
      data-busy="1"
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        ...(fullscreen
          ? { position: 'absolute', inset: 0, background: 'rgba(0,0,0,.72)' }
          : { padding: '48px 0' }),
      }}
    >
      {/*
        Il nome del canale **prima** del video: è la quarta cosa che §5 chiede allo zapping, ed è
        quella che lo rende sopportabile — si sa dove si è arrivati mentre l'immagine non c'è ancora.
      */}
      {title && (
        <div style={{ font: `700 34px ${FONT_STACK}`, color: colors.text, textAlign: 'center' }}>
          {title}
        </div>
      )}

      <Spinner />

      <div style={{ font: `600 21px ${FONT_STACK}`, color: colors.text, textAlign: 'center' }}>
        {label}
      </div>

      {hint && (
        <div style={{ font: `400 17px ${FONT_STACK}`, color: colors.textDim, textAlign: 'center' }}>
          {hint}
        </div>
      )}

      {back && (
        <div style={{ font: `400 15px ${FONT_STACK}`, color: colors.textMuted, textAlign: 'center' }}>
          {back}
        </div>
      )}
    </div>
  );
}

/**
 * Il disco che gira, in CSS e senza immagini.
 *
 * L'animazione è dichiarata qui dentro con un `<style>` invece che in un foglio globale: questo
 * pacchetto viene impacchettato dentro tre contenitori diversi (browser, Tizen, webOS) e un foglio
 * che va importato a parte è una cosa che il terzo contenitore dimenticherà — e la rotella
 * sparirebbe senza che niente protesti.
 */
function Spinner(): ReactNode {
  return (
    <>
      <style>{`@keyframes palco-spin { to { transform: rotate(360deg) } }`}</style>
      <div
        data-spinner="1"
        aria-hidden
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: `4px solid ${colors.border}`,
          borderTopColor: colors.accent,
          animation: 'palco-spin 900ms linear infinite',
        }}
      />
    </>
  );
}
